// Lazy Firebase bootstrap, ported from apps/old-web/lib/firebase.ts.
//
// HARD RULE: the firebase SDK is NEVER imported at module scope. It loads as
// its own chunk only after analytics.ts has verified config AND consent, so
// declined/GDPR-pending visitors never download a byte of it. With the
// VITE_FIREBASE_* env unset the whole stack is a silent no-op — the app stays
// deployable before the Firebase project / GA property exists.

import type { FirebaseApp } from "firebase/app";
import type { Analytics } from "firebase/analytics";

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  measurementId: string;
}

/** Read lazily (not at module scope) so tests can stub the env per case. */
export function firebaseConfig(): FirebaseWebConfig | null {
  const env = import.meta.env as Record<string, string | undefined>;
  const apiKey = env.VITE_FIREBASE_API_KEY?.trim();
  const authDomain = env.VITE_FIREBASE_AUTH_DOMAIN?.trim();
  const projectId = env.VITE_FIREBASE_PROJECT_ID?.trim();
  const appId = env.VITE_FIREBASE_APP_ID?.trim();
  const measurementId = env.VITE_FIREBASE_MEASUREMENT_ID?.trim();
  if (!apiKey || !authDomain || !projectId || !appId || !measurementId) return null;
  return {
    apiKey,
    authDomain,
    projectId,
    appId,
    measurementId
  };
}

export function isAnalyticsConfigured(): boolean {
  return firebaseConfig() !== null;
}

let appInstance: FirebaseApp | null = null;
let analyticsInstance: Analytics | null = null;
let collectionSetter: ((analytics: Analytics, enabled: boolean) => void) | null = null;

/**
 * Firebase Analytics instance: browser-only, lazily initialized, null when
 * config is missing or the environment is unsupported (e.g. no cookies).
 * Callers MUST have checked consent first — this function loads the SDK.
 */
export async function getFirebaseAnalytics(safePageLocation: string): Promise<Analytics | null> {
  if (typeof window === "undefined") return null;
  if (analyticsInstance) return analyticsInstance;
  const config = firebaseConfig();
  if (!config) return null;

  const { initializeApp, getApps } = await import("firebase/app");
  appInstance = appInstance ?? getApps()[0] ?? initializeApp(config);

  const { initializeAnalytics, isSupported, setAnalyticsCollectionEnabled } =
    await import("firebase/analytics");
  const supported = await isSupported().catch(() => false);
  if (!supported) return null;

  // Never let GA/Firebase infer the browser's real URL. These apps carry
  // order/access capabilities in paths, queries, or fragments. Automatic page
  // measurement is disabled, and the initial config receives only the
  // caller-normalized location.
  analyticsInstance = initializeAnalytics(appInstance, {
    config: { send_page_view: false, page_location: safePageLocation }
  });
  collectionSetter = setAnalyticsCollectionEnabled;
  return analyticsInstance;
}

/** Stop an already-created instance when a visitor withdraws consent. */
export function setFirebaseAnalyticsCollectionAllowed(allowed: boolean): void {
  if (!analyticsInstance || !collectionSetter) return;
  collectionSetter(analyticsInstance, allowed);
}
